import { useEffect, useState } from 'react';
import logService, { ActionCategory } from '../services/logging/logService';
import { fetchUserInfo } from '../services/userService';

/**
 * Component for initializing and managing the logging service
 * Should be placed high in the component tree (e.g., in App.tsx or main layout)
 */
const LoggerInitializer: React.FC = () => {
  const [initialized, setInitialized] = useState(false);

  // Monitora i cambiamenti nel token/utente per inizializzare il logger  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        
        if (token && userStr) {
          // Utilizza i dati direttamente da localStorage se disponibili
          try {
            const user = JSON.parse(userStr);
            // Inizializza il logger con i dati utente
            console.log('Initializing logger with user:', user);
            logService.init(user);
            
            // Log della navigazione iniziale
            const currentPath = window.location.pathname;
            logService.logAction(
              ActionCategory.NAVIGATION,
              'page_view',
              `Initial navigation to ${currentPath}`,
              { 
                initial: true,
                hash: window.location.hash,
                search: window.location.search,
                path: currentPath
              }
            );
            
            setInitialized(true);
          } catch (e) {
            console.error('Error parsing user data from localStorage:', e);
            await fetchAndInitUser(token);
          }
        } else if (token) {
          // Se abbiamo solo il token, recupera i dati utente dall'API
          await fetchAndInitUser(token);
        } else {
          // Nessun token disponibile, non inizializziamo il logger
          console.log('No authentication token found');
          setInitialized(false);
        }
      } catch (error) {
        console.error('Error initializing logger:', error);
        setInitialized(false);
      }
    };

    // Funzione ausiliaria per recuperare i dati utente e inizializzare il logger
    const fetchAndInitUser = async (token: string) => {
      try {
        const userInfo = await fetchUserInfo();
        if (userInfo && userInfo.user) {
          console.log('Fetched user data:', userInfo.user);
          logService.init(userInfo.user);
          setInitialized(true);
        }
      } catch (error) {
        console.error('Failed to fetch user info:', error);
        setInitialized(false);
      }
    };

    // Inizia il processo di controllo
    checkAuth();

    // Aggiungi un listener per il localStorage per rilevare login/logout
    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === 'user') {
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Gestisce l'evento di logout/chiusura della finestra
  useEffect(() => {
    if (initialized) {
      // Registriamo il gestore di beforeunload a livello globale
      const handleBeforeUnload = () => {
        // Questo verrà eseguito quando l'utente chiude la finestra o naviga via
        console.log('BeforeUnload event detected, logging session end');
        
        // Forzare l'esecuzione sincrona del logout
        try {
          const now = Date.now();
          const sessionDuration = Math.floor((now - logService['sessionStartTime']) / 1000);
          
/*           const logData = {
            level: 'info',
            serviceName: 'cediFe',
            message: {
              action: 'authentication:logout',
              description: 'Session ended (beforeunload)',
              timestamp: new Date().toISOString(),
              sessionId: logService['sessionId'],
              sessionDuration,
              userInfo: logService['userInfo']
            }
          };
          
          navigator.sendBeacon('/nodeapi/writeLog', 
            new Blob([JSON.stringify(logData)], {type: 'application/json'})
          ); */
          
          console.log('Session end log sent via beacon API');
        } catch (e) {
          console.error('Error logging session end:', e);
        }
      };

      // Per il logout esplicito, aggiungi un listener per la rimozione del token
      const detectLogout = () => {
        const currentToken = localStorage.getItem('token');
        if (!currentToken && initialized) {
          console.log('Token removed, logging logout event');
          // Esegui il logout in modo diretto tramite API beacon
          try {
            const now = Date.now();
            const sessionDuration = Math.floor((now - logService['sessionStartTime']) / 1000);
            
/*             const logData = {
              level: 'info',
              serviceName: 'cediFe',
              message: {
                action: 'authentication:logout',
                description: 'Session ended (token removed)',
                timestamp: new Date().toISOString(),
                sessionId: logService['sessionId'],
                sessionDuration,
                userInfo: logService['userInfo']
              }
            };
            
            // Invia direttamente tramite chiamata fetch sincrona
            console.log('Sending logout event via direct API call');
            navigator.sendBeacon('/nodeapi/writeLog', 
              new Blob([JSON.stringify(logData)], {type: 'application/json'})
            ); */
            
            // Resetta lo stato per evitare rilevamenti duplicati
            setInitialized(false);
          } catch (e) {
            console.error('Error during logout logging:', e);
          }
        }
      };

      // Controlla a intervalli regolari se il token è stato rimosso
      const logoutInterval = setInterval(detectLogout, 2000);
      
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        clearInterval(logoutInterval);
      };
    }
  }, [initialized]);

  // Questo componente non renderizza nulla
  return null;
};

export default LoggerInitializer;